import React, { useMemo, useState } from 'react';

import emailjs from '@emailjs/browser';
import Script from 'dangerous-html/react';

const ContactSection: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const emailConfig = useMemo(
    () => ({
      serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
      templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
      publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
    }),
    []
  );

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSending(true);
    setFeedback(null);

    if (!emailConfig.serviceId || !emailConfig.templateId || !emailConfig.publicKey) {
      setFeedback({
        type: 'error',
        message: 'Email service is not configured. Please try again later.',
      });
      setIsSending(false);
      return;
    }

    try {
      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          from_phone: formData.phone,
          from_company: formData.company,
          message: formData.message,
          date: new Date().toLocaleString(),
        },
        emailConfig.publicKey
      );
      setFeedback({
        type: 'success',
        message: 'Thanks! Your message has been sent successfully.',
      });
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      setShowForm(false);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Something went wrong while sending your message. Please try again.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    setFeedback(null);
  };

  return (
    <section id="contact" className="contact-wrapper">
      <div className="contact-content">
        <div className="contact-decoration-left"></div>
        <div className="contact-decoration-right"></div>
        <div className="contact-card">
          <div className="contact-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
            >
              <g
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              >
                <path d="m22 7l-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                <rect width="20" height="16" x="2" y="4" rx="2"></rect>
              </g>
            </svg>
          </div>
          <h2 className="section-title">
            Contact
          </h2>
          <p className="contact-description">
            For inquiries about architecture, generative AI, or cloud delivery, please get in touch for a consultation or proposal.
          </p>
          <div className="contact-engagement-models">
            <div className="contact-engagement-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 6L9 17l-5-5"
                ></path>
              </svg>
              <span>Leadership Engagement</span>
            </div>
            <div className="contact-engagement-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 6L9 17l-5-5"
                ></path>
              </svg>
              <span>Consulting Projects</span>
            </div>
            <div className="contact-engagement-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 6L9 17l-5-5"
                ></path>
              </svg>
              <span>Technical Advisory</span>
            </div>
            <div className="contact-engagement-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 6L9 17l-5-5"
                ></path>
              </svg>
              <span>Discuss An Opportunity</span>
            </div>
          </div>
          <div className="contact-cta-group contact-direct-lines">
            <button
              className="btn btn-primary btn-xl"
              onClick={() => setShowForm(true)}
              type="button"
            >
              Send Message
            </button>
            <a
              href="https://www.linkedin.com/in/anuj-kumar-tiwari-04"
              className="contact-direct-link"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 8a6 6 0 1 1-10.814 3.972M8 22h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4Z"
                ></path>
              </svg>
              <span>Connect on LinkedIn</span>
            </a>
          </div>
          {showForm && (
            <form className="contact-form" onSubmit={handleSendMessage}>
              <div className="contact-form-field">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="contact-phone">Phone</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="contact-company">Company</label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
                <small className="contact-form-hint">Please keep your message under 50 KB.</small>
              </div>
              <div className="contact-form-actions">
                <button className="btn btn-primary" type="submit" disabled={isSending}>
                  {isSending ? 'Sending...' : 'Send'}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={handleCancel}
                  disabled={isSending}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {feedback && (
            <div className={`contact-feedback contact-feedback-${feedback.type}`}>
              {feedback.message}
            </div>
          )}
          <div className="contact-response-note">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20 6L9 17l-5-5"
              ></path>
            </svg>
            <span>All inquiries typically receive a response within 24 hours.</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
